import { Control, Controller } from "react-hook-form";
import MuiAutocomplete from "@material-ui/lab/Autocomplete";
import { Checkbox, RadioGroup, TextField, Chip, FormControlLabel, Radio } from "@material-ui/core";
import { useState } from "react";

export type AutocompleteFieldOption = {
  label: string;
  value: string;
};

export interface AutocompleteFieldProps {
  required?: boolean;
  disabled?: boolean;
  multiple?: boolean;
  label?: string | React.ReactNode;
  id?: string;
  name?: string;
  defaultValue?: AutocompleteFieldOption[];
  control: Control<any>;
  controllerName?: string;
  options: AutocompleteFieldOption[];
  freeSolo?: boolean;
  placeholder?: string;
  helperText?: string;
  errorMessage?: string;
  disableCloseOnSelect?: boolean;
  selectBoxOnChange?: (event: React.ChangeEvent<{}>) => void;
  textFieldOnChange?: (event: React.ChangeEvent<{}>) => void;

}

export const AutocompleteField = (props: AutocompleteFieldProps) => {
  const multiple = (props.multiple === undefined) ? true : props.multiple;
  const controllerName = (props.controllerName ||
    props.name ||
    props.id) as string;

  const [autoCompleteValue, setAutoCompleteValue] = useState(
    (props.defaultValue || []) as AutocompleteFieldOption[]);

  const renderCustomChip = (autoCompleteValue: any, field, params: any) => {
    if (multiple) {
      return params.InputProps.startAdornment
    } else {
      const isSomeOptionMarkedAsChecked = (field?.value?.length > 0);
      if (isSomeOptionMarkedAsChecked) {
        /* This clears the text that was typed before and prevents new texts from 
        being typed in the autocomplete field when an option is checked. */
        params.inputProps.value = '';
      }
      if (autoCompleteValue?.value) {
        const showClearButtonInTag = (isDisabled) => {
          const field: any = {}
          if (!isDisabled) {
            field.onDelete = () => {
              props.control.setValues(controllerName, [], {
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: true,
              });
              setAutoCompleteValue([])
              field.onChange([])
            }
          }
          return field
        }
        return (
          <Chip
            label={autoCompleteValue.value}
            {...showClearButtonInTag(props.disabled)}
            size="medium"
          />
        );
      }
      return undefined;
    }
  }

  const selectBoxOnChangeCustom = (event: React.ChangeEvent<{}>): void => {
    if (props.selectBoxOnChange) {
      return props.selectBoxOnChange(event);
    }
  }

  const textFieldOnChangeCustom = (event: React.ChangeEvent<{}>): void => {
    if (props.textFieldOnChange) {
      return props.textFieldOnChange(event);
    }
  }

  return (
    <Controller
      name={controllerName}
      control={props.control}
      render={({ field }) => (
        <MuiAutocomplete
          disabled={props.disabled}
          fullWidth
          multiple={multiple}
          placeholder={props.placeholder}
          freeSolo={props.freeSolo}
          value={autoCompleteValue}
          options={props.options}
          disableCloseOnSelect={props.disableCloseOnSelect}
          getOptionLabel={(option: AutocompleteFieldOption) => {
            return multiple ? option.label : option.label ? option.label : ''
          }}
          getOptionSelected={(
            option: AutocompleteFieldOption,
            value: AutocompleteFieldOption
          ) => value.value === option.value}
          onChange={(_, newValue) => {
            const prepareValues = (item) => {
              return typeof item === "object" ? item : {
                label: item,
                value: item,
              }
            };
            const getValue = (newValue) => {
              if (props.freeSolo) {
                multiple ?
                  newValue.map((newValueItem) => prepareValues(newValueItem)) :
                  prepareValues(newValue)
              }
              return newValue;
            }
            const getControllerValues = (value) => {
              value = multiple ? value : value ? [value] : [];
              return (value as AutocompleteFieldOption[]).map(
                (item: AutocompleteFieldOption) => item.value
              );
            }

            const value = getValue(newValue);
            const controllerValues = getControllerValues(value);

            setAutoCompleteValue(value);
            props.control.setValues(controllerName, controllerValues, {
              shouldValidate: true,
              shouldDirty: true,
              shouldTouch: true,
            });
            field.onChange(controllerValues);
            selectBoxOnChangeCustom(_)
          }}
          renderOption={(option, { selected }) => {
            return (
              <>
                {multiple ?
                  <>
                    <Checkbox
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    {option.label}
                  </>
                  :
                  <>
                    <RadioGroup
                      style={{ marginRight: 8 }}
                    />
                    <FormControlLabel value={option.value} control={<Radio checked={selected} />} label={option.label} />
                  </>
                }
              </>
            )
          }}
          renderInput={(params) => {
            return (
              <TextField
                {...params}
                InputProps={{
                  ...params.InputProps,
                  className: props.disabled ? 'text-text-gray-300 bg-gray-100' : '',
                  startAdornment: renderCustomChip(autoCompleteValue, field, params),
                }}
                required={props.required}
                variant="outlined"
                label={props.label}
                error={props.errorMessage ? true : false}
                helperText={
                  props.errorMessage ? props.errorMessage : props.helperText
                }
                onChange={textFieldOnChangeCustom}
              />
            )
          }
          }
          renderTags={(tagValue, getTagProps) => {
            return (
              <span className="inline">
                {tagValue.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    label={option.label}
                  />
                ))}
              </span>
            );
          }}
        />
      )}
    />
  );
};
